import { NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { routes } from './app-routing.module';
import { AppComponent } from './app.component';

/* Angular Material */
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';

/* Angular */
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';

import { provideZxvbnServiceForPSM } from 'angular-password-strength-meter/zxcvbn';
import { PasswordStrengthComponent } from './pages/password-strength/password-strength.component';

/* ngrx */
import { StoreModule, ActionReducer, MetaReducer } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

/* Auth Pages */
import { LoginComponent } from './pages/auth/login/login.component';
import { RegisterComponent } from './pages/auth/register/register.component';
import { ForgotPasswordComponent } from './pages/auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';
import { LinkedinComponent } from './pages/auth/oauth/linkedin/linkedin.component';
import { AccountSetupComponent } from './pages/auth/account-setup/account-setup.component';
import { GoogleComponent } from './pages/auth/oauth/google/google.component';

/* Header */
import { HeaderRightSideComponent } from './common/header-right-side/header-right-side.component';
import { HeaderComponent } from './common/header/header.component';

/* Dashboard Pages */
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DashboardFilterComponent } from './pages/dashboard/dashboard-filter/dashboard-filter.component';
import { RequestAssistanceComponent } from './pages/dashboard/request-assistance/request-assistance.component';

/* Dashboard State */
import { DashboardEffects } from './state/dashboard/dashboard.effects';

/* Reports */
import { ReportsComponent } from './pages/reports/reports.component';
import { ReportsFilterComponent } from './pages/reports/reports-filter/reports-filter.component';
import { ReportExportConfirmModalComponent } from './pages/reports/report-export-confirm-modal/report-export-confirm-modal.component';
import { ConfirmModalComponent } from './pages/reports/confirm-modal/confirm-modal.component';

/* Workspace */
import { WorkspaceComponent } from './pages/workspace/workspace.component';
import { WorkspaceReportsComponent } from './pages//workspace/workspace-reports/workspace-reports.component';

/* Report */
import {ReportComponent} from './pages/report/report.component'
import {PersonInsightsComponent} from "./pages/report/person-insights/person-insights.component";
import {ProfileHeaderComponent} from "./pages/report/profile-header/profile-header.component";
import {CompanyInsightsComponent} from "./pages/report/company-insights/company-insights.component";
import {IndustryInsightsComponent} from "./pages/report/industry-insights/industry-insights.component";
import {AssistantComponent} from "./pages/report/aside/assistant/assistant.component";
import {EditComponent} from "./pages/report/aside/edit/edit.component";
import {NotesComponent} from "./pages/report/aside/notes/notes.component";
import {CrmComponent} from "./pages/report/aside/crm/crm.component";
import {BackgroundComponent} from "./pages/report/person-insights/background/background.component";
import {ExpertiseComponent} from "./pages/report/person-insights/expertise/expertise.component";
import {PersonContactComponent} from "./pages/report/person-insights/person-contact/person-contact.component";
import {EditContactModalComponent} from "./pages/report/person-insights/person-contact/edit-contact-modal/edit-contact-modal.component";
import {CompanyContactComponent} from "./pages/report/company-insights/company-contact/company-contact.component";
import {TeamComponent} from "./pages/report/company-insights/team/team.component";
import {EcosystemComponent} from "./pages/report/company-insights/ecosystem/ecosystem.component";
import {IndustryLandscapeComponent} from "./pages/report/industry-insights/industry-landscape/industry-landscape.component";
import {PersonExpandedComponent} from "./pages/report/profile-header/person-expanded/person-expanded.component";
import {CompanyExpandedComponent} from "./pages/report/profile-header/company-expanded/company-expanded.component";
import {CompanyDetailsModalComponent} from "./pages/advanced-search/company-details-modal/company-details-modal.component";
import {ReportHeaderComponent} from "./pages/report/report-header/report-header.component";
import {ShareComponent} from "./pages/report/share/share.component";
import {ReportTagsComponent} from "./pages/report/report-tags/report-tags.component";
import {IcpScoreComponent} from "./pages/report/profile-header/company-expanded/icp-score/icp-score.component";
import {SpecialInformationComponent} from "./pages/report/special-information/special-information.component";
import {EditPromptComponent} from "./pages/report/edit-prompt/edit-prompt.component";
import {ReportLoaderComponent} from "./pages/report/report-loader/report-loader.component";
import {PulseLoadingComponent} from "./pages/report/pulse-loading/pulse-loading.component";
import {ChangeCompanyModalComponent} from "./pages/report/change-company-modal/change-company-modal.component";
import {ChangeCompanyComponent} from "./pages/report/profile-header/company-expanded/change-company/change-company.component";
import {EditRevenueComponent} from "./pages/report/profile-header/company-expanded/edit-revenue/edit-revenue.component";

/* Market Insight */
import {MarketInsightComponent} from "./pages/market-insight/market-insight.component";

/* Market Intelligence Report */
import {MarketIntelligenceReportComponent} from "./pages/market-intelligence-report/market-intelligence-report.component";

/* Settings */
import { ProfileComponent } from './pages/settings/profile/profile.component';
import { BillingProfileComponent } from './pages/settings/billing-profile/billing-profile.component';
import { InvoicesComponent } from './pages/settings/invoices/invoices.component';
import { AccountCancellationComponent } from './pages/settings/account/account-cancellation/account-cancellation.component';
import { UpdateAccountSetupComponent } from './pages/settings/update-account-setup/update-account-setup.component';
import { AccountTabsComponent } from './pages/settings/account-tabs/account-tabs.component';

/* Tooltip */
import { MatTooltipModule } from '@angular/material/tooltip';
import { InfoTooltipComponent } from './common/info-tooltip/info-tooltip.component';
import { CustomTooltipComponent } from './common/custom-tooltip/custom-tooltip.component';

/* Common */
import { FooterComponent } from './common/footer/footer.component';
import { EditButtonComponent } from './common/edit-button/edit-button.component';
import { SubscriptionBannerComponent } from './common/subscription-banner/subscription-banner.component';
import { LanguageHeaderComponent } from './common/language-header/language-header.component';
import { DarkModeToggleComponent } from './common/dark-mode-toggle/dark-mode-toggle.component';
import { NotificationComponent } from './common/notification/notification.component';
import { FeedbackComponent } from './common/feedback/feedback.component';
import { ModalComponent } from './common/feedback/modal/modal.component';
import { SearchPersonDropdownComponent } from './common/search-person/search-person-dropdown/search-person-dropdown.component';
import { ReportBannerComponent } from './common/report-banner/report-banner.component';
import { SearchPersonComponent } from './common/search-person/search-person.component';
import { PaginationComponent } from './common/pagination/pagination.component';
// import { SidebarComponent } from './common/sidebar/sidebar.component';
import { LoaderComponent } from './common/loader/loader.component';
import { SubscriptionHeaderComponent } from './common/subscription-header/subscription-header.component';

/* Form */
import { InputFieldComponent } from './common/form/input-field/input-field.component';
import { SelectFieldComponent } from './common/form/select-field/select-field.component';
import { DropdownSelectComponent } from './common/form/dropdown-select/dropdown-select.component';
import { ComboboxMultiselectComponent } from './common/form/combobox-multiselect/combobox-multiselect.component';

/* Public profile */
import { PublicProfileComponent } from './pages/public-profile/public-profile.component';

/* Page not found */
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';

import { TestComponent } from './test/test.component';
import { UserListComponent } from './state-example/user-list/user-list.component';
import { UserCollectionComponent } from './state-example/user-collection/user-collection.component';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SlickCarouselModule } from 'ngx-slick-carousel';

import { reducers } from './state/reducers';

/* Effects */
import { AuthEffects } from './state/auth/auth.effects';
import { OAuthEffects } from './state/oauth/oauth.effects';
import { SearchEffects } from './state/search/search.effects';
import { ReportEffects } from './state/report/report.effects';
import { AccountEffects } from './state/account/account.effects';
import { GdprNoticeEffects } from './state/gdprNotice/gdprNotice.effects';
import { ListEffects } from './state/list/list.effects';
import { BillingEffects } from './state/billing/billing.effects';
import { UserProfileEffects } from './state/userProfile/userProfile.effects';
import { SubscriptionEffects } from './state/subscription/subscription.effects';
import { ProfileListEffects } from './state/profileList/profileList.effects';
import { BillingProfileEffects } from './state/billingProfile/billingProfile.effects';
import { SerpApiEffects } from './state/serpApi/serpApi.effects';
import { TagEffects } from './state/tag/tag.effects';
import { PsyTestingEffects } from './state/psyTesting/psyTesting.effects';

import { localStorageSync } from 'ngrx-store-localstorage';
import { resetState } from './state/global/global.actions';

export interface AppState {
  auth: any;
  oauth: any;
  subscription: any;
  search: any;
  company: any;
  ui: any;
  advancedSearch: any;
}

// Root reducer with reset capability
export function rootReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return (state, action) => {
    // When resetState action is dispatched, return undefined
    // This will cause all reducers to return their initial state
    if (action.type === resetState.type) {
      // Clear localStorage to ensure sync is also reset
      localStorage.clear();
      sessionStorage.clear();
      state = undefined;
    }
    return reducer(state, action);
  };
}

// Local storage sync meta-reducer
export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return (state: any, action: any) => {
    const nextState = localStorageSync({
      keys: ['auth', 'oauth', 'subscription', 'search', 'company', 'ui', 'advancedSearch', 'workspace'],
      rehydrate: true
    })(reducer)(state, action);

    return nextState;
  };
}

// Define meta-reducers array - ORDER IS IMPORTANT!
// rootReducer must be first to handle reset before localStorageSync
const metaReducers: Array<MetaReducer<any, any>> = [rootReducer, localStorageSyncReducer];

/*
export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({ keys: ['auth', 'oauth', 'subscription', 'search', 'company', 'ui', 'advancedSearch'], rehydrate: true })(reducer);
}
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];
*/

import { NgApexchartsModule } from 'ng-apexcharts';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { ZXingScannerModule } from '@zxing/ngx-scanner';

// Pipes
import {GetAssistantPromptPipe} from 'src/app/shared/get-assistant-prompt.pipe';
import {FilterByIdPipe} from 'src/app/pipes/filter-by-id.pipe';

import {AssistantPromptComponent} from "./common/assistant-prompt/assistant-prompt.component";
import {CompanyInformationComponent} from "./pages/report/company-insights/company-information/company-information.component";
import {TruncatePipe} from 'src/app/pipes/truncate.pipe';
import {DepartmentJobTypesComponent} from "./pages/report/charts/department-job-types/department-job-types.component";
import {AreaOfExpertiseComponent} from "./pages/report/charts/area-of-expertise/area-of-expertise.component";
import {TagsComponent} from "./pages/report/aside/tags/tags.component";
import {EmailsComponent} from "./pages/report/company-insights/emails/emails.component";
import {EmailsPaginationComponent} from "./pages/report/company-insights/emails/emails-pagination/emails-pagination.component";
import {IcpScoreHeaderComponent} from "./pages/report/icp-score-header/icp-score-header.component";
import {SqlQualificationComponent} from "./pages/report/sql-qualification/sql-qualification.component";

import {AdvancedSearchComponent} from './pages/advanced-search/advanced-search.component';
import {FilterDropdownComponent} from './shared/filter-builder/filter-dropdown/filter-dropdown.component';
import {TypeaheadLocationInputComponent } from './shared/filter-builder/typeahead-location-input/typeahead-location-input.component';
import {FilterInputComponent} from './shared/filter-builder/filter-input/filter-input.component';

import { CompaniesComponent } from './pages/advanced-search/companies/companies.component';
import { PeopleComponent } from './pages/advanced-search/companies/people/people.component';
import {AdvancedSearchEffects} from "./state/advancedSearch/advanced-search.effects";
import {FilterRangeComponent} from "./shared/filter-builder/filter-range/filter-range.component";
import {FilterProximityComponent} from "./pages/advanced-search/filter-proximity/filter-proximity.component";
import {FilterTagsComponent} from "./shared/filter-builder/filter-tags/filter-tags.component";
import {ReportSidebarComponent} from "./common/report-sidebar/report-sidebar.component";
import {BulkUploadComponent} from "./pages/dashboard/bulk-upload/bulk-upload.component";
import {LivespaceConnectDialogComponent} from "./components/livespace-connect-dialog/livespace-connect-dialog.component";
import {HubspotEffects} from "./state/hubspot/hubspot.effects";
import {LivespaceEffects} from "./state/livespace/livespace.effects";
import {GoogleEffects} from "./state/google/google.effects";
import {TourEffects} from "./state/tour/tour.effects";

import {TourComponent} from "./tour/tour.component";
import {
    FilterComboboxMultiselectComponent
} from "./shared/filter-builder/filter-combobox-multiselect/filter-combobox-multiselect.component";
import {EnrichProfileComponent} from "./pages/report/enrich-profile/enrich-profile.component";
import {AiAgentToolBoxComponent} from "./pages/report/ai-agent-tool-box/ai-agent-tool-box.component";
import {SqlQualifyWizardComponent} from "./pages/report/sql-qualify-wizard/sql-qualify-wizard.component";
import {PeopleRecommendationsComponent} from "./pages/report/people-recommendations/people-recommendations.component";

import {WorkspaceEffects} from "./state/workspace/workspace.effects";
import {DashboardWorkspacesComponent} from "./pages/dashboard/dashboard-workspaces/dashboard-workspaces.component";
import {DashboardTopLeadsComponent} from "./pages/dashboard/dashboard-top-leads/dashboard-top-leads.component";
import {EditWorkspaceComponent} from "./pages/workspace/edit-workspace/edit-workspace.component";
import {DeleteWorkspaceModalComponent} from "./pages/workspace/delete-workspace-modal/delete-workspace-modal.component";
import {ChangeWorkspaceComponent} from "./pages/workspace/workspace-reports/change-workspace/change-workspace.component";

import {AuthSidePanelComponent} from "./pages/auth/auth-side-panel/auth-side-panel.component";
import {HeadersInterceptor} from "./shared/guards/headers.interceptor";
import {CrmEffects} from "./state/crm/crm.effects";
import { MarketingEffects } from './state/marketing/marketing.effects';
import { IcpEffects } from './state/icp/icp.effects';
import { OnboardingEffects } from './state/onboarding/onboarding.effects';
import { VeridionExportEffects } from './state/veridionExport/veridion-export.effects';
import { SearchExportEffects } from './state/searchExport/search-export.effects';
import { DailyLeadLegacyEffects } from './state/dailyLeadLegacy/daily-lead.effects';
import { LeadPipelineEffects } from './state/leadPipeline/lead-pipeline.effects';

// Function to load translations
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RegisterComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    DashboardComponent,
    SearchPersonComponent,
    PaginationComponent,
    ProfileComponent,
    BillingProfileComponent,
    FooterComponent,
    ReportsComponent,
    WorkspaceComponent,
    WorkspaceReportsComponent,
    AdvancedSearchComponent,
    ReportComponent,
    PublicProfileComponent,
    LinkedinComponent,
    PageNotFoundComponent,
    NotificationComponent,
    FeedbackComponent,
    ModalComponent,
    AccountSetupComponent,
    GoogleComponent,
    ReportHeaderComponent,
    PersonInsightsComponent,
    ProfileHeaderComponent,
    CompanyInsightsComponent,
    IndustryInsightsComponent,
    SpecialInformationComponent,
    AssistantComponent,
    EditComponent,
    NotesComponent,
    CrmComponent,
    EmailsComponent,
    ReportComponent,
    MarketInsightComponent,
    MarketIntelligenceReportComponent,
    BackgroundComponent,
    ExpertiseComponent,
    PersonContactComponent,
    CompanyContactComponent,
    TeamComponent,
    EcosystemComponent,
    IndustryLandscapeComponent,
    EditButtonComponent,
    PersonExpandedComponent,
    CompanyExpandedComponent,
    ProfileHeaderComponent,
    // SidebarComponent,
    LoaderComponent,
    InvoicesComponent,
    TestComponent,
    UserListComponent,
    UserCollectionComponent,
    UpdateAccountSetupComponent,
    GetAssistantPromptPipe,
    TruncatePipe,
    ChangeWorkspaceComponent,
  ],
  imports: [
    StoreModule.forRoot(reducers, {metaReducers}),
    EffectsModule.forRoot([
      AuthEffects,
      OAuthEffects,
      DashboardEffects,
      SearchEffects,
      ReportEffects,
      AccountEffects,
      GdprNoticeEffects,
      BillingEffects,
      UserProfileEffects,
      SubscriptionEffects,
      ListEffects,
      BillingProfileEffects,
      SerpApiEffects,
      ProfileListEffects,
      TagEffects,
      PsyTestingEffects,
      AdvancedSearchEffects,
      HubspotEffects,
      LivespaceEffects,
      GoogleEffects,
      TourEffects,
      WorkspaceEffects,
      CrmEffects,
      MarketingEffects,
      IcpEffects,
      OnboardingEffects,
      VeridionExportEffects,
      SearchExportEffects,
      DailyLeadLegacyEffects,
      LeadPipelineEffects,
    ]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    RouterModule.forRoot(routes),
    StoreDevtoolsModule.instrument({maxAge: 25}),
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    SlickCarouselModule,
    BrowserModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    PasswordStrengthComponent,
    ShareComponent,
    AccountCancellationComponent,
    NgApexchartsModule,
    SubscriptionBannerComponent,
    SubscriptionHeaderComponent,
    ReportBannerComponent,
    LanguageHeaderComponent,
    DarkModeToggleComponent,
    AccountTabsComponent,
    MatTooltipModule,
    InfoTooltipComponent,
    InputFieldComponent,
    SelectFieldComponent,
    DropdownSelectComponent,
    ComboboxMultiselectComponent,
    DashboardFilterComponent,
    ReportTagsComponent,
    IcpScoreComponent,
    ReportsFilterComponent,
    ReportExportConfirmModalComponent,
    ConfirmModalComponent,
    EditPromptComponent,
    ReportLoaderComponent,
    PulseLoadingComponent,
    RequestAssistanceComponent,
    CustomTooltipComponent,
    ChangeCompanyComponent,
    ChangeCompanyModalComponent,
    ZXingScannerModule,
    SearchPersonDropdownComponent,
    AssistantPromptComponent,
    HeaderRightSideComponent,
    HeaderComponent,
    CompanyInformationComponent,
    DepartmentJobTypesComponent,
    AreaOfExpertiseComponent,
    TagsComponent,
    EmailsPaginationComponent,
    IcpScoreHeaderComponent,
    SqlQualificationComponent,
    CompaniesComponent,
    PeopleComponent,
    FilterByIdPipe,
    FilterDropdownComponent,
    TypeaheadLocationInputComponent,
    FilterInputComponent,
    FilterRangeComponent,
    FilterProximityComponent,
    FilterTagsComponent,
    ReportSidebarComponent,
    BulkUploadComponent,
    LivespaceConnectDialogComponent,
    TourComponent,
    FilterComboboxMultiselectComponent,
    EnrichProfileComponent,
    AiAgentToolBoxComponent,
    SqlQualifyWizardComponent,
    PeopleRecommendationsComponent,
    AuthSidePanelComponent,
    EditWorkspaceComponent,
    DeleteWorkspaceModalComponent,
    DashboardWorkspacesComponent,
    DashboardTopLeadsComponent,
    CompanyDetailsModalComponent,
    EditRevenueComponent,
    EditContactModalComponent,
  ],
  providers: [
    provideZxvbnServiceForPSM(),
    provideAnimationsAsync(),
    { provide: 'IS_DEVELOPMENT', useValue: isDevMode() },
    { provide: HTTP_INTERCEPTORS, useClass: HeadersInterceptor, multi: true },
  ],
  exports: [
    ProfileHeaderComponent,
    PaginationComponent,
    SearchPersonComponent,
    ReportHeaderComponent,
    GetAssistantPromptPipe,
    TruncatePipe,
    FilterDropdownComponent,
    TypeaheadLocationInputComponent

  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
